/**
 * InviteNotificationBanner Component
 *
 * Displays pending battle challenges and lobby invites as a persistent banner.
 * Shows countdown timer and allows accept/decline actions.
 */

import { useState } from 'react';
import { useInviteNotifications, useCountdown } from '../hooks/useInviteNotifications';
import type { InviteNotification } from '@drawn-of-war/shared';
import styles from './InviteNotificationBanner.module.css';

/**
 * Single invite notification item
 */
interface InviteItemProps {
  invite: InviteNotification;
  onAccept: () => void;
  onDecline: () => void;
  isLoading: boolean;
}

function InviteItem({ invite, onAccept, onDecline, isLoading }: InviteItemProps) {
  const timeLeft = useCountdown(invite.expiresAt);
  const isExpired = timeLeft === 'Expired';

  const isChallenge = invite.type === 'challenge';
  const icon = isChallenge ? '⚔️' : '📨';
  const message = isChallenge
    ? `${invite.from.displayName} challenged you to a battle!`
    : `${invite.from.displayName} invited you to their lobby!`;

  // Show if battle is password-protected
  const isPasswordProtected = invite.isPublic === false && invite.password;

  return (
    <div className={styles.inviteItem}>
      <div className={styles.inviteContent}>
        <span className={styles.inviteIcon}>{icon}</span>
        <span className={styles.inviteMessage}>{message}</span>
        {invite.battleName && (
          <span className={styles.battleName}>"{invite.battleName}"</span>
        )}
        {isPasswordProtected && (
          <span className={styles.passwordInfo}>
            🔒 Password: <code className={styles.passwordCode}>{invite.password}</code>
          </span>
        )}
      </div>

      <div className={styles.inviteTimer} data-expired={isExpired}>
        {isExpired ? 'Expired' : `${timeLeft}`}
      </div>

      <div className={styles.inviteActions}>
        <button
          className={`${styles.actionButton} ${styles.accept}`}
          onClick={onAccept}
          disabled={isLoading || isExpired}
        >
          Accept
        </button>
        <button
          className={`${styles.actionButton} ${styles.decline}`}
          onClick={onDecline}
          disabled={isLoading}
        >
          Decline
        </button>
      </div>
    </div>
  );
}

/**
 * InviteNotificationBanner Component
 *
 * Shows all pending invites with navigation for multiple invites.
 */
export function InviteNotificationBanner() {
  const { invites, acceptInvite, declineInvite, isLoading, error, clearError } = useInviteNotifications();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Don't render if no invites
  if (invites.length === 0) {
    return null;
  }

  // Ensure current index is valid
  const safeIndex = Math.min(currentIndex, invites.length - 1);
  const currentInvite = invites[safeIndex];

  // Navigation handlers
  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % invites.length);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + invites.length) % invites.length);
  };

  const handleAccept = async () => {
    await acceptInvite(currentInvite.id);
    // Index will auto-correct on next render
  };

  const handleDecline = async () => {
    await declineInvite(currentInvite.id);
    // Index will auto-correct on next render
  };

  return (
    <div className={styles.banner}>
      <div className={styles.bannerHeader}>
        <span className={styles.notificationIcon}>🔔</span>
        <span className={styles.notificationLabel}>NOTIFICATION</span>
        {invites.length > 1 && (
          <span className={styles.inviteCount}>
            {safeIndex + 1} of {invites.length}
          </span>
        )}
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
          <button className={styles.dismissError} onClick={clearError}>×</button>
        </div>
      )}

      <InviteItem
        invite={currentInvite}
        onAccept={handleAccept}
        onDecline={handleDecline}
        isLoading={isLoading}
      />

      {invites.length > 1 && (
        <div className={styles.navigation}>
          <button
            className={styles.navButton}
            onClick={goPrev}
            aria-label="Previous invite"
          >
            ← Prev
          </button>
          <button
            className={styles.navButton}
            onClick={goNext}
            aria-label="Next invite"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
